import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { ClientBilling } from 'entities';

import { Column, Row, Skeleton, Label } from 'components/Blocks';

import { WarningIcon } from 'components/Icons';

const multiLineParser: (value: string | string[]) => ReactNode = (text) => (
  <ul style={{ listStyle: 'circle', paddingLeft: '24px', paddingRight: '12px' }}>
    {(text as string).split('\n').map((text, index) => (
      <li key={text} style={{ listStyle: 'circle', marginTop: index ? '4px' : 0 }}>
        {text}
      </li>
    ))}
  </ul>
);

const infoRenderer: Array<{
  property: keyof ClientBilling;
  Container: typeof Column | typeof Row;
  getLabel: (t: TFunction) => string;
  parser?: (value: string | string[]) => ReactNode;
}> = [
  {
    property: 'companyName',

    getLabel: (t) => t('company-name'),

    Container: Row,
  },
  {
    property: 'preferredCurrency',

    getLabel: (t) => t('preferred-currency'),

    Container: Row,
  },
  {
    property: 'emails',

    getLabel: (t) => t('emails'),

    Container: Row,

    parser: (emails) => (emails as string[]).join(', '),
  },
  {
    property: 'preferredDueDate',

    getLabel: (t) => t('preferred-due-date'),

    Container: Row,
  },
  {
    property: 'contactDetails',

    getLabel: (t) => t('contact-details'),

    Container: Column,

    parser: multiLineParser,
  },
  {
    property: 'comment',

    getLabel: (t) => t('comments'),

    Container: Column,

    parser: multiLineParser,
  },
];

interface ClientBillingReference {
  clientName: string;
  data?: ClientBilling;
  loading?: boolean;
}

export function ClientBillingReference({ clientName, data, loading }: ClientBillingReference) {
  const { t } = useLang();

  return (
    <Column fullWidth className="self-stretch overflow-hidden rounded-md border bg-muted">
      <Column gap="2" className="scrollbar h-full overflow-y-auto p-3">
        <p className="text-center font-medium">{t('client-billing-info')}</p>

        <Row gap="1.5">
          <Label>{t('client')}:</Label>

          <span className="text-justify">{clientName}</span>
        </Row>

        {data ? (
          <Column gap="2">
            {infoRenderer.map(({ property, Container, getLabel, parser }) => (
              <Container gap="1.5" key={`${property}--view`}>
                <Label>{getLabel(t)}:</Label>

                <span className="text-justify">{parser?.(data[property]) || data[property]}</span>
              </Container>
            ))}
          </Column>
        ) : loading ? (
          <Skeleton height={410} />
        ) : (
          <Column gap="2" alignItems="center" justifyContent="center" className="flex-1">
            <WarningIcon color="action" />

            <span className="text-center">{t('info-not-available')}</span>
          </Column>
        )}
      </Column>
    </Column>
  );
}
